// Transaction v1 (SIMD-0385) assembly, byte for byte solana-message 4.4.1
// `v1::Message::try_compile_with_config` + `serialize`, and the pre-flight
// rules of the Rust client's `v1` module. Pinned by sdk/fixtures/golden-v1.json
// and jupiter-assembly-v1.json (test/v1.test.ts).

import { type AccountMetaSpec, hex, u16le, u32le, u64le } from './digest.js';
import { concat } from './derive.js';

export const V1_PREFIX = 0x81;
export const MAX_TRANSACTION_SIZE = 4096;
export const MAX_ADDRESSES = 64;
export const MAX_INSTRUCTIONS = 64;
export const SIGNATURE_SIZE = 64;
/** Config mask bits: priority fee (two words), compute-unit limit, loaded-data limit, heap. */
export const MASK = { PRIORITY_FEE: 0b11, COMPUTE_UNIT_LIMIT: 0b100, LOADED_ACCOUNTS_DATA_SIZE: 0b1000, HEAP_SIZE: 0b10000 } as const;

export interface Instruction {
  programId: Uint8Array;
  accounts: AccountMetaSpec[];
  data: Uint8Array;
}

/** In v1 an unset limit means zero, so every field is explicit. */
export interface Limits {
  computeUnitLimit: number;
  loadedAccountsDataSizeLimit: number;
  /** Absolute lamports, not micro-lamports per CU. */
  priorityFeeLamports: bigint;
}

export interface CompiledInstruction {
  programIdIndex: number;
  accountIndexes: number[];
  data: Uint8Array;
}

export interface V1Message {
  numRequiredSignatures: number;
  numReadonlySigned: number;
  numReadonlyUnsigned: number;
  limits: Limits;
  recentBlockhash: Uint8Array;
  /** Payer first, then writable signers, readonly signers, writable non-signers, readonly non-signers; each group sorted by bytes. */
  accountKeys: Uint8Array[];
  instructions: CompiledInstruction[];
}

export type V1ErrorCode = 'TooLarge' | 'TooManyAddresses' | 'TooManyInstructions' | 'TooManySignatures' | 'LimitUnset' | 'BadLength' | 'BadPrefix' | 'BadMask' | 'Truncated' | 'TrailingBytes' | 'BadHeader' | 'DuplicateKey' | 'BadIndex';

export class V1Error extends Error {
  constructor(readonly code: V1ErrorCode, message: string) {
    super(message);
    this.name = 'V1Error';
  }
}

function compareBytes(a: Uint8Array, b: Uint8Array): number {
  for (let i = 0; i < 32; i++) if (a[i] !== b[i]) return a[i]! - b[i]!;
  return 0;
}

/** Compile instructions into a v1 message the way the Rust client does (`CompiledKeys` ordering). */
export function compileV1(payer: Uint8Array, instructions: Instruction[], recentBlockhash: Uint8Array, limits: Limits): V1Message {
  if (payer.length !== 32 || recentBlockhash.length !== 32) throw new V1Error('BadLength', 'payer and blockhash are 32 bytes');
  const metas = new Map<string, { key: Uint8Array; isSigner: boolean; isWritable: boolean }>();
  const touch = (key: Uint8Array, isSigner: boolean, isWritable: boolean) => {
    if (key.length !== 32) throw new V1Error('BadLength', 'account keys are 32 bytes');
    const h = hex(key);
    const m = metas.get(h) ?? { key, isSigner: false, isWritable: false };
    m.isSigner ||= isSigner;
    m.isWritable ||= isWritable;
    metas.set(h, m);
  };
  for (const ix of instructions) {
    touch(ix.programId, false, false);
    for (const a of ix.accounts) touch(a.pubkey, a.isSigner, a.isWritable);
  }
  touch(payer, true, true);
  const payerHex = hex(payer);
  const rest = [...metas.values()].filter((m) => hex(m.key) !== payerHex).sort((a, b) => compareBytes(a.key, b.key));
  const writableSigners = [payer, ...rest.filter((m) => m.isSigner && m.isWritable).map((m) => m.key)];
  const readonlySigners = rest.filter((m) => m.isSigner && !m.isWritable).map((m) => m.key);
  const writableNonSigners = rest.filter((m) => !m.isSigner && m.isWritable).map((m) => m.key);
  const readonlyNonSigners = rest.filter((m) => !m.isSigner && !m.isWritable).map((m) => m.key);
  const accountKeys = [...writableSigners, ...readonlySigners, ...writableNonSigners, ...readonlyNonSigners];
  if (accountKeys.length > 255) throw new V1Error('TooManyAddresses', `${accountKeys.length} addresses`);
  const index = new Map(accountKeys.map((k, i) => [hex(k), i] as const));
  const at = (k: Uint8Array) => index.get(hex(k))!;
  return {
    numRequiredSignatures: writableSigners.length + readonlySigners.length,
    numReadonlySigned: readonlySigners.length,
    numReadonlyUnsigned: readonlyNonSigners.length,
    limits,
    recentBlockhash,
    accountKeys,
    instructions: instructions.map((ix) => ({ programIdIndex: at(ix.programId), accountIndexes: ix.accounts.map((a) => at(a.pubkey)), data: ix.data })),
  };
}

export function configMask(): number {
  return MASK.PRIORITY_FEE | MASK.COMPUTE_UNIT_LIMIT | MASK.LOADED_ACCOUNTS_DATA_SIZE;
}

/** 0x81 || header (3) || mask u32 le || blockhash || num_ix u8 || num_keys u8 || keys || fee u64 le || cu u32 le || loaded u32 le || ix headers || ix bodies. */
export function serializeV1(m: V1Message): Uint8Array {
  const headers = m.instructions.map((ix) => concat(Uint8Array.of(ix.programIdIndex, ix.accountIndexes.length), u16le(ix.data.length)));
  const bodies = m.instructions.map((ix) => concat(Uint8Array.from(ix.accountIndexes), ix.data));
  return concat(
    Uint8Array.of(V1_PREFIX, m.numRequiredSignatures, m.numReadonlySigned, m.numReadonlyUnsigned),
    u32le(configMask()),
    m.recentBlockhash,
    Uint8Array.of(m.instructions.length, m.accountKeys.length),
    ...m.accountKeys,
    u64le(m.limits.priorityFeeLamports),
    u32le(m.limits.computeUnitLimit),
    u32le(m.limits.loadedAccountsDataSizeLimit),
    ...headers,
    ...bodies,
  );
}

/** Serialised transaction size: the message plus one 64-byte signature per required signer. */
export function transactionSize(m: V1Message): number {
  return serializeV1(m).length + SIGNATURE_SIZE * m.numRequiredSignatures;
}

export interface Estimate {
  bytes: number;
  uniqueAddresses: number;
  signatures: number;
}

/** Formula-based estimate from the wire format, without serialising (equal to `transactionSize` after compile). */
export function estimate(payer: Uint8Array, instructions: Instruction[]): Estimate {
  const addresses = new Set<string>([hex(payer)]);
  const signers = new Set<string>([hex(payer)]);
  let ixBytes = 0;
  for (const ix of instructions) {
    addresses.add(hex(ix.programId));
    for (const a of ix.accounts) {
      addresses.add(hex(a.pubkey));
      if (a.isSigner) signers.add(hex(a.pubkey));
    }
    ixBytes += 4 + ix.accounts.length + ix.data.length;
  }
  const configWords = 2 + 1 + 1;
  return { bytes: 64 * signers.size + 1 + 3 + 4 + 4 * configWords + 32 + 1 + 32 * addresses.size + 1 + ixBytes, uniqueAddresses: addresses.size, signatures: signers.size };
}

/** The pre-flight rules (docs/01 section 13, invariant 10): caps, size and explicit limits. */
export function checkV1(m: V1Message): void {
  if (m.accountKeys.length > MAX_ADDRESSES) throw new V1Error('TooManyAddresses', `${m.accountKeys.length} addresses, at most ${MAX_ADDRESSES}`);
  if (m.instructions.length > MAX_INSTRUCTIONS) throw new V1Error('TooManyInstructions', `${m.instructions.length} instructions`);
  if (m.numRequiredSignatures > 12 || m.numRequiredSignatures === 0) throw new V1Error('TooManySignatures', `${m.numRequiredSignatures} signatures`);
  const bytes = transactionSize(m);
  if (bytes > MAX_TRANSACTION_SIZE) throw new V1Error('TooLarge', `${bytes} bytes, at most ${MAX_TRANSACTION_SIZE}`);
  if (m.limits.computeUnitLimit === 0 || m.limits.loadedAccountsDataSizeLimit === 0) throw new V1Error('LimitUnset', 'compute-unit and loaded-data limits must be set');
}

/**
 * The protocol maximum for `loadedAccountsDataSizeLimit`, 64 MiB: the figure
 * swaps and token-touching transactions have requested since M4, and that
 * mainnet accepted (the 2026-09-24 wrap and the 2026-09-28 swaps carry it:
 * docs/evidence/mainnet/2026-09-24/step-3-wrap.json,
 * docs/evidence/mainnet/2026-09-28/swap-1-sol-to-usdc.json).
 */
export const MAX_LOADED_ACCOUNTS_DATA_SIZE = 64 * 1024 * 1024;

/**
 * `loadedAccountsDataSizeLimit` for the plain transactions, the ones that
 * load only the System program and the vault program (Initialize, deposit,
 * native withdrawal and transfer, key refresh, recovery, Close, Reopen, the
 * gas tank's send-out): the protocol maximum, a selected conservative policy
 * (R70; R73).
 *
 * A live cluster meters 64 bytes plus the data of every account key, plus
 * the vault's whole ProgramData (a 45-byte header and the entire, possibly
 * extended, ELF region); litesvm and the local shim do not count ProgramData,
 * so only a live cluster shows the limit (`loaded-data.ts`). The ceiling was
 * 64 KiB until 2026-09-28, when the fee-side upgrade's extended ProgramData
 * (74,397 B) outgrew it, then 128 KiB. R70 showed that a ceiling sized to the
 * accounts as they are does not hold: anyone may extend ProgramData while the
 * program stays upgradeable, up to the 10 MiB account cap, without changing a
 * byte of the trusted ELF, and a withdrawal may credit any address, even a
 * LoaderV3 Program account of up to 10 MiB whose ProgramData holds up to
 * 10 MiB more. By the bounds of their keys' roles the plain shapes need at
 * most 31,457,828 B with the app's generated fee payer, which holds no data
 * (`PLAIN_SHAPES` in `loaded-data.ts`), and at most 31,457,908 B with a payer
 * the caller supplies, which may be an 80-byte System nonce account
 * (`PLAIN_EXTERNAL_PAYER_WORST_CASE`); its tests print both. Both would also
 * fit 32 MiB, so the maximum is not a necessity for these shapes: it is the
 * figure chosen for margin, the one the token-touching transactions and swaps
 * already request. The requested figure is not part of the fee: a v1 fee is
 * the base fee plus `priorityFeeLamports`.
 */
export const PLAIN_LOADED_ACCOUNTS_DATA_SIZE = MAX_LOADED_ACCOUNTS_DATA_SIZE;

/**
 * Limits from a simulation (docs/01 section 7.1 step 6): CU = ceil(1.2 x consumed), at most 400,000;
 * loaded data = the protocol maximum. Until R70 the loaded-data figure was the next 32 KiB page above
 * the simulated total plus 64 KiB; but the one-time key has already signed when the simulation runs,
 * and a permissionless ProgramData extension landing between the simulation and the transaction would
 * then fail it and burn the leaf. `loadedBytes` (the cluster's SIMD-0186 figure from
 * `simulateTransaction`) is only checked: a total above the maximum cannot land at any limit.
 */
export function limitsFromSimulation(consumedCu: number, loadedBytes: number, priorityFeeLamports: bigint): Limits {
  if (!(loadedBytes >= 0 && loadedBytes <= MAX_LOADED_ACCOUNTS_DATA_SIZE)) {
    throw new RangeError(`simulated loaded data ${loadedBytes} B is outside 0..${MAX_LOADED_ACCOUNTS_DATA_SIZE} B; no transaction can carry it`);
  }
  return {
    computeUnitLimit: Math.min(Math.ceil((consumedCu * 12) / 10), 400_000),
    loadedAccountsDataSizeLimit: MAX_LOADED_ACCOUNTS_DATA_SIZE,
    priorityFeeLamports,
  };
}

/**
 * The limits of the plain transactions, the ones that load only the System
 * program and the vault program (Activate, the key refresh, the native
 * withdrawal, the fee reserve's send-out and the sweep): 200,000 compute
 * units, the plain loaded-data figure above, and a 10,000-lamport absolute
 * priority fee. Activate uses these figures with `computeUnitLimit` set to
 * 200,000 + r, r drawn per attempt (fee-reserve.ts `activationLimitsFrom`,
 * docs/18-one-fee-reserve.md 5.1 D step 7), which tells two Initialize
 * carriers on one blockhash apart unless the draws are equal; every other
 * plain transaction uses them as they are. The app's engine uses these figures (app/src/engine.ts
 * `PLAIN_LIMITS`), the worker compiles every sweep with them (worker.ts
 * `sweep`, docs/18-one-fee-reserve.md 2.4), and the fee vectors' fixture
 * message carries them (sdk/fixtures/fee-derivation-v1.json).
 */
export const PLAIN_LIMITS: Limits = { computeUnitLimit: 200_000, loadedAccountsDataSizeLimit: PLAIN_LOADED_ACCOUNTS_DATA_SIZE, priorityFeeLamports: 10_000n };

/**
 * The strict parser of docs/18-one-fee-reserve.md 2.4 (R81): the exact inverse
 * of `serializeV1`, reading the whole layout and nothing else. The mask must
 * equal `configMask()`, so the three limit values are always present in their
 * fixed order; every read is bounds-checked, and a byte left over after the
 * last instruction body is a refusal, so the buffer is consumed exactly. The
 * structure checks that follow make `accountKeys[0]` a writable signer and
 * every index meaningful: at least one key; 1 <= required signatures <= keys;
 * read-only signed < required signatures; read-only unsigned <= keys - required
 * signatures; no key repeated; every program and account index below the key
 * count; no program index 0 (the payer is not a program). `checkV1` is a
 * separate step the worker's `feeSign` runs on the result, and so is the
 * canonical check that `serializeV1(parseV1(bytes))` equals `bytes`.
 */
export function parseV1(bytes: Uint8Array): V1Message {
  let off = 0;
  const need = (n: number, what: string): void => {
    if (off + n > bytes.length) throw new V1Error('Truncated', `${what}: ${n} byte(s) needed at offset ${off}, ${bytes.length - off} left`);
  };
  const byte = (what: string): number => {
    need(1, what);
    return bytes[off++]!;
  };
  const take = (n: number, what: string): Uint8Array => {
    need(n, what);
    const out = bytes.slice(off, off + n);
    off += n;
    return out;
  };
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  const prefix = byte('version prefix');
  if (prefix !== V1_PREFIX) throw new V1Error('BadPrefix', `first byte 0x${prefix.toString(16)} is not the v1 prefix 0x81`);
  const numRequiredSignatures = byte('header');
  const numReadonlySigned = byte('header');
  const numReadonlyUnsigned = byte('header');
  need(4, 'config mask');
  const mask = view.getUint32(off, true);
  off += 4;
  if (mask !== configMask()) throw new V1Error('BadMask', `config mask 0x${mask.toString(16)} is not the fixed mask 0x${configMask().toString(16)}`);
  const recentBlockhash = take(32, 'blockhash');
  const numInstructions = byte('instruction count');
  const numKeys = byte('key count');
  const accountKeys: Uint8Array[] = [];
  for (let i = 0; i < numKeys; i++) accountKeys.push(take(32, `key ${i}`));
  need(8, 'priority fee');
  const priorityFeeLamports = view.getBigUint64(off, true);
  off += 8;
  need(4, 'compute-unit limit');
  const computeUnitLimit = view.getUint32(off, true);
  off += 4;
  need(4, 'loaded-data limit');
  const loadedAccountsDataSizeLimit = view.getUint32(off, true);
  off += 4;
  const headers: { programIdIndex: number; numAccounts: number; dataLen: number }[] = [];
  for (let i = 0; i < numInstructions; i++) {
    const programIdIndex = byte(`instruction ${i} header`);
    const numAccounts = byte(`instruction ${i} header`);
    need(2, `instruction ${i} header`);
    const dataLen = view.getUint16(off, true);
    off += 2;
    headers.push({ programIdIndex, numAccounts, dataLen });
  }
  const instructions: CompiledInstruction[] = headers.map((h, i) => ({
    programIdIndex: h.programIdIndex,
    accountIndexes: Array.from(take(h.numAccounts, `instruction ${i} account indexes`)),
    data: take(h.dataLen, `instruction ${i} data`),
  }));
  if (off !== bytes.length) throw new V1Error('TrailingBytes', `${bytes.length - off} byte(s) left after the last instruction body`);

  if (numKeys === 0) throw new V1Error('BadHeader', 'a message needs at least one key');
  if (numRequiredSignatures < 1 || numRequiredSignatures > numKeys) throw new V1Error('BadHeader', `${numRequiredSignatures} required signatures for ${numKeys} key(s)`);
  if (numReadonlySigned >= numRequiredSignatures) throw new V1Error('BadHeader', `${numReadonlySigned} read-only signers of ${numRequiredSignatures}: the payer must be a writable signer`);
  if (numReadonlyUnsigned > numKeys - numRequiredSignatures) throw new V1Error('BadHeader', `${numReadonlyUnsigned} read-only unsigned keys, more than the ${numKeys - numRequiredSignatures} unsigned key(s)`);
  const seen = new Set<string>();
  for (const key of accountKeys) {
    const h = hex(key);
    if (seen.has(h)) throw new V1Error('DuplicateKey', `key ${h} appears twice`);
    seen.add(h);
  }
  instructions.forEach((ix, i) => {
    if (ix.programIdIndex >= numKeys) throw new V1Error('BadIndex', `instruction ${i}: program index ${ix.programIdIndex} is not below ${numKeys}`);
    if (ix.programIdIndex === 0) throw new V1Error('BadIndex', `instruction ${i}: the payer (key 0) is not a program`);
    for (const a of ix.accountIndexes) if (a >= numKeys) throw new V1Error('BadIndex', `instruction ${i}: account index ${a} is not below ${numKeys}`);
  });

  return { numRequiredSignatures, numReadonlySigned, numReadonlyUnsigned, limits: { computeUnitLimit, loadedAccountsDataSizeLimit, priorityFeeLamports }, recentBlockhash, accountKeys, instructions };
}
